/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the collapsibletable component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    accordionSummaryClasses,
    Chip,
    CircularProgress,
    darken,
    styled,
    Typography
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

import { default as pluralize } from 'pluralize';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a table
 */
import Table, { ITableProps } from './TablePremium';

/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

export interface ICollapsibleTableProps<Type extends object = any> extends ITableProps<Type> {
    title: string;
    pluralizeTitle?: boolean;
    pluralizeEntity?: boolean;
    loading: boolean;
    entityName?: string;
    startCollapsed?: boolean;
    disabledTableText?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledExpansionPanelContainer = styled(Accordion)(() => ({
    // marginTop: theme.spacing(2),
    // marginBottom: theme.spacing(2)
}))

const StyledHeader = styled(AccordionSummary)(({ theme }) => ({
    background: theme.palette.primary.main,
    color: theme.palette.common.white,

    [`& .${accordionSummaryClasses.content}`]: {
        justifyContent: 'space-between'
    },

    [`&.${accordionSummaryClasses.expanded}`]: {
        minHeight: 48,
        
        [`& .${accordionSummaryClasses.content}`]: {
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(1.5)
        }
    },
}))

const StyledContent = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0)
}))

const StyledChip =  styled(Chip)(({ theme }) => ({
    backgroundColor: darken(theme.palette.secondary.main, 0.2),
    color: theme.palette.common.white,
    marginRight: theme.spacing(1)
}))


const StyledIcon =  styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.common.white
}))

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const CollapsibleTablePremium = <Type extends object>({
    title,
    pluralizeTitle,
    pluralizeEntity,
    loading,
    entityName,
    startCollapsed,
    disabledTableText,
    rows,
    ...tableProps
}: ICollapsibleTableProps<Type>) => {
    const disabled = !loading && (!rows || rows.length === 0);

    const [expanded, setExpanded] = React.useState(startCollapsed !== true);

    const onClick = React.useCallback(() => {
        setExpanded(!expanded)
    }, [expanded, setExpanded]);

    const upperCasePlural = (pluralizeTitle ?? true) ? pluralize.plural(title) : title;

    const lowerCasePlural = upperCasePlural.toLowerCase();

    const entityNameToUse = entityName ?
        entityName :
        title;

    return (
        <>
            <StyledExpansionPanelContainer
                disabled={disabled}
                expanded={expanded && !disabled && !loading}
            >
                <StyledHeader
                    onClick={onClick}
                    expandIcon={loading ? <CircularProgress color="secondary" size={16} /> : disabled ? false : <StyledIcon fontSize={16} icon={faChevronDown} fixedWidth />}
                    aria-controls={`${title}-content`}
                    id={`${title}-header`}
                >
                    <Typography>
                        {upperCasePlural}
                    </Typography>
                    {
                        disabled && (
                            <Typography>
                                {
                                    disabledTableText ? disabledTableText : `There are no ${lowerCasePlural}`
                                }
                            </Typography>
                        )
                    }
                    {
                        !loading && rows && !disabled && (
                            <StyledChip
                                label={`${pluralizeEntity === false ? (rows.length + " " + entityNameToUse) : pluralize(entityNameToUse, rows.length, true)}`}
                                size="small"
                            />
                        )
                    }
                </StyledHeader>
                <StyledContent>
                    <Table
                        {...tableProps}
                        rows={rows ?? []}
                    />
                </StyledContent>
            </StyledExpansionPanelContainer>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default CollapsibleTablePremium;
